/**
 * Cycle Reports Page
 * 
 * Displays reports for all cycles with progress and outcome summaries.
 */

import { ObjectRef } from '@/components/ds/ObjectRef';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { PHASE_ORDER } from '../../../types/prioritization/degapCycle';

export function CycleReports() {
  const cycles = useDegapProcessStore(state => state.cycles);
  const sessions = useSessionStore(state => state.sessions);
  
  // Enrich cycles with session data
  const enrichedCycles = useMemo(() => {
    return cycles.map(cycle => {
      const cycleSessions = sessions.filter(s => s.cycleId === cycle.id);
      const completedSessions = cycleSessions.filter(s => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED');
      const publishedSessions = cycleSessions.filter(s => s.state === 'PUBLISHED');
      
      const phaseIndex = PHASE_ORDER.indexOf(cycle.currentPhase);
      const phaseProgress = ((phaseIndex + 1) / PHASE_ORDER.length) * 100;
      
      return {
        ...cycle,
        sessionCount: cycleSessions.length,
        completedSessionCount: completedSessions.length,
        publishedSessionCount: publishedSessions.length,
        phaseIndex,
        phaseProgress,
      };
    }).sort((a, b) => {
      // Active first, then by date
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (b.status === 'active' && a.status !== 'active') return 1;
      return (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0);
    });
  }, [cycles, sessions]);
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <PlayCircleIcon className="h-5 w-5 text-[var(--green)]" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-[var(--text-ter)]" />;
      case 'paused':
        return <PauseCircleIcon className="h-5 w-5 text-[var(--mustard)]" />;
      default:
        return <ArrowPathIcon className="h-5 w-5 text-[var(--text-ter)]" />;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/prioritization/reports"
          className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-3"
        >
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Back to Reports
        </Link>
        <PageHeader
          icon={<ChartBarIcon className="h-4 w-4 text-[var(--text-sec)]" />}
          iconBg="bg-[var(--hover-tint)]"
          title="Cycle Reports"
          subtitle="View progress and outcomes for all prioritization cycles"
          mb="mb-0"
        />
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--green-dim)]">
              <PlayCircleIcon className="h-6 w-6 text-[var(--green)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">
                {enrichedCycles.filter(c => c.status === 'active').length}
              </p>
              <p className="text-sm text-[var(--text-ter)]">Active Cycles</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--slate-dim)]">
              <CheckCircleIcon className="h-6 w-6 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">
                {enrichedCycles.filter(c => c.status === 'completed').length}
              </p>
              <p className="text-sm text-[var(--text-ter)]">Completed Cycles</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--slate-dim)]">
              <ClipboardDocumentListIcon className="h-6 w-6 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">
                {enrichedCycles.reduce((sum, c) => sum + c.completedSessionCount, 0)}
              </p>
              <p className="text-sm text-[var(--text-ter)]">Completed Sessions</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Cycle List */}
      {enrichedCycles.length === 0 ? (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8 text-center">
          <ArrowPathIcon className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">No Cycles Yet</h3>
          <p className="text-[var(--text-ter)]">
            Create a cycle to get started with prioritization.
          </p>
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
          <table className="min-w-full divide-y divide-[var(--rule)]">
            <thead className="bg-[var(--surface)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
                  Cycle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
                  Current Phase
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--bg-raised)] divide-y divide-[var(--rule)]">
              {enrichedCycles.map(cycle => (
                <tr key={cycle.id} className="hover:bg-[var(--surface)]">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium"><ObjectRef kind="cycle" id={cycle.id} name={cycle.name} /></div>
                      {cycle.description && (
                        <div className="text-xs text-[var(--text-ter)] truncate max-w-xs">
                          {cycle.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(cycle.status)}
                      <span className="text-sm text-[var(--text-sec)] capitalize">{cycle.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
                      {cycle.currentPhase}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-32">
                      <div className="flex items-center justify-between text-xs text-[var(--text-ter)] mb-1">
                        <span>{cycle.phaseIndex + 1}/{PHASE_ORDER.length}</span>
                        <span>{Math.round(cycle.phaseProgress)}%</span>
                      </div>
                      <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                        <div 
                          className="bg-[var(--slate)] h-2 rounded-full"
                          style={{ width: `${cycle.phaseProgress}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[var(--text-pri)]">
                      {cycle.completedSessionCount}/{cycle.sessionCount}
                    </div>
                    <div className="text-xs text-[var(--text-ter)]">
                      {cycle.publishedSessionCount} published
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/prioritization/cycles/${cycle.id}/results`}
                      className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                    >
                      <ChartBarIcon className="h-4 w-4" />
                      View Results
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CycleReports;
